import { Method, ContentType } from './Extra';
import Options from './Options';
import MethodInfo from './MethodInfo';
import { EnvironmentVariables } from '../../constants/EnvironmentVariables';
import { ClientBase, HttpClientSelector } from './HttpClientSelector';
import { pBase, rModelsBase } from '../models/.catalog';
import { Store } from '../../redux/StoreContainer';
import { SecurityService } from '../../services/security';

export class ApiBase<TIn extends pBase, TOut extends rModelsBase> {
  constructor(methodInfo: MethodInfo, options?: Options) {
    this._BaseUrl = EnvironmentVariables.Api_Has_Proxy
      ? ''
      : EnvironmentVariables.Api_Default_Base_Url;
    this._ServiceUrl = EnvironmentVariables.Api_Default_Service_Url;
    this._MethodUrl = methodInfo.MethodUrl;
    this._Method = methodInfo.MethodType || Method.POST;
    this._ContentType = methodInfo.ContentType || ContentType.JSON;
  }

  protected _HttpClient: ClientBase = HttpClientSelector;

  private _BaseUrl: string;
  public get BaseUrl(): string {
    return this._BaseUrl;
  }

  protected _ServiceUrl: string;
  public get ServiceUrl(): string {
    return this._ServiceUrl;
  }

  protected _MethodUrl: string;
  public get MethodUrl(): string {
    return this._MethodUrl;
  }

  private _Method: string;
  public get Method(): string {
    return this._Method;
  }

  public get Versioning(): string {
    return `${SecurityService.URLSafeParams([{ name: 'v', value: EnvironmentVariables.Build_Code }])}`;
  }

  private _ContentType: string;
  public get ContentType(): string {
    return this._ContentType;
  }

  private _PostData: TIn | null = null;
  public getPostData(): TIn | null {
    return this._PostData || null;
  }
  public setPostData(input: TIn) {
    this._PostData = input;
  }

  private _RequestData: {
    baseUrl: string;
    serviceUrl: string;
    methodUrl: string;
    data?: any;
    contentType?: string;
  } | null = null;
  private set RequestData(req: {
    baseUrl: string;
    serviceUrl: string;
    methodUrl: string;
    data?: any;
    contentType?: string;
  }) {
    this._RequestData = req;
  }
  public getRequestData(): {
    baseUrl: string;
    serviceUrl: string;
    methodUrl: string;
    data?: any;
    contentType?: string;
  } | null {
    return this._RequestData;
  }

  private _ResultData: Promise<TOut> | null = null;
  private set ResultData(res: Promise<TOut>) {
    this._ResultData = res;
  }
  public getResultData(): Promise<TOut> | null {
    return this._ResultData || null;
  }

  protected TransformInput = (input: TIn): object => {
    return { args: input.ToObject() };
  };

  public Request(data: TIn): Promise<TOut> {
    switch (this.Method) {
      case Method.GET:
        return (this.ResultData = this._HttpClient.Get<TOut>(
          (this.RequestData = {
            baseUrl: this.BaseUrl,
            serviceUrl: this.ServiceUrl,
            methodUrl: this.MethodUrl + this.Versioning,
          })
        ));
      case Method.POST:
        return (this.ResultData = this._HttpClient.Post<TOut>(
          (this.RequestData = {
            baseUrl: this.BaseUrl,
            serviceUrl: this.ServiceUrl,
            methodUrl: this.MethodUrl + this.Versioning,
            data: this.TransformInput(data),
            contentType: this.ContentType,
          })
        ));
      default:
        return (this.ResultData = this._HttpClient.Get<TOut>(
          (this.RequestData = {
            baseUrl: this.BaseUrl,
            serviceUrl: this.ServiceUrl,
            methodUrl: this.MethodUrl + this.Versioning,
          })
        ));
    }
  }
}

export class RaporApiBase extends ApiBase<pBase, any> {
  private SHOW_URL = EnvironmentVariables.RAPOR_SHOW_URL;
  private DOWNLOAD_URL = EnvironmentVariables.RAPOR_DOWNLOAD_URL;

  public getUrl = () => {
    return (
      (this.SHOW_URL || '') +
      (this.MethodUrl || '') +
      `&${SecurityService.AddURLSafeParams([{ name: 'ticket', value: Store.getState().oturumAcReducer.Data?.Ticket || '' }])}`
    );
  };

  public getDownloadUrl = () => {
    return `${this.MethodUrl}&${SecurityService.AddURLSafeParams([{ name: 'Download', value: true }, { name: 'ticket', value: Store.getState().oturumAcReducer.Data?.Ticket || '' }])}`;
  };

  constructor() {
    super(
      new MethodInfo({
        MethodUrl: ``,
        MethodType: Method.GET,
      })
    );
  }

  public RequestRapor = (exportType: 'Pdf' | 'Xlsx'): Promise<any> => {
    return this._HttpClient.Get<any>({
      baseUrl: this.BaseUrl,
      serviceUrl: this.DOWNLOAD_URL,
      methodUrl: `${this.getDownloadUrl()}&${SecurityService.AddURLSafeParams([{ name: 'ExportType', value: exportType }])}`,
    });
  };
}

export { default as Options } from './Options';
export { default as MethodInfo } from './MethodInfo';
export { Method, Mode, ContentType } from './Extra';
